import React, {
  useEffect,
  useState,
  useRef,
  MouseEvent,
  useMemo,
  lazy,
  Suspense,
} from "react";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "../../../components/ui/alert";
import { LinkingData } from "./ExtractionPdfViewer";
import CSVViewer from "./CsvViewer";
import { ZoomIn, ZoomOut, RotateCw, Maximize } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { pdfDocument } from "../../../models/extraction";
import { z } from "zod";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";

// Lazy load PDF viewer to reduce initial bundle size
const ExtractionPdfViewer = lazy(() =>
  import("./ExtractionPdfViewer").then((module) => ({
    default: module.default,
  })),
);

// Lazy load Excel viewer to reduce initial bundle size
const ExcelViewer = lazy(() =>
  import("./ExcelViewer").then((module) => ({
    default: module.default,
  })),
);

export type FileType =
  | "pdf"
  | "jpeg"
  | "jpg"
  | "png"
  | "webp"
  | "csv"
  | "xlsx"
  | "xlsm";

export type BoundingBox = [
  [number, number],
  [number, number],
  [number, number],
  [number, number],
];

// Utility function to convert coordinate array to x, y, width, height format
const convertBoundingBoxToRect = (boundingBox: BoundingBox) => {
  const xCoords = boundingBox.map(([x]) => x);
  const yCoords = boundingBox.map(([, y]) => y);

  const minX = Math.min(...xCoords);
  const maxX = Math.max(...xCoords);
  const minY = Math.min(...yCoords);
  const maxY = Math.max(...yCoords);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

export type FileViewerProps = {
  signedUrl: string;
  fileType: FileType;
  fileName?: string;
  initialPage?: number;
  boundingBox?: BoundingBox;
  elementId?: string;
  allowLinking?: boolean;
  /**
   * The original width of the page (in the same coordinate space as the bounding box).
   * This is required for FileViewer to internally scale the bounding box to the rendered
   * canvas dimensions. If omitted, the bounding box will be rendered without additional
   * scaling which may result in mis-alignment.
   */
  pageWidth?: number;

  /**
   * The original height of the page (in the same coordinate space as the bounding box).
   * This is required for FileViewer to internally scale the bounding box to the rendered
   * canvas dimensions. If omitted, the bounding box will be rendered without additional
   * scaling which may result in mis-alignment.
   */
  pageHeight?: number;
  defaultSearchValue?: string;
  documentExtraction?: z.infer<typeof pdfDocument>;
  onLinkingComplete?: (linkingData: LinkingData) => void;
};

type ImageViewerProps = Omit<FileViewerProps, "fileType" | "initialPage"> & {
  alt?: string;
  setCanvasHeight?: (height: number) => void;
  setCanvasWidth?: (width: number) => void;
};

const ImageViewer: React.FC<ImageViewerProps> = ({
  signedUrl,
  boundingBox,
  alt,
  setCanvasHeight,
  setCanvasWidth,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.3, 5));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.3, 0.1));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale((prev) => Math.max(0.5, Math.min(prev + delta, 3)));
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }

    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Report canvas (image) size back to the parent so that it can calculate
  // the appropriate bounding-box scaling. We do this both on initial load and
  // whenever the container gets resized.
  useEffect(() => {
    if (!containerRef.current || !imageRef.current) return;

    const reportSize = () => {
      if (imageRef.current) {
        const { clientHeight, clientWidth } = imageRef.current;
        setCanvasHeight?.(clientHeight);
        setCanvasWidth?.(clientWidth);
      }
    };

    // Initial report
    reportSize();

    // Observe future resizes
    const resizeObserver = new ResizeObserver(() => reportSize());
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [setCanvasHeight, setCanvasWidth]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex flex-col relative bg-stone-50 overflow-hidden"
      onWheel={handleWheel}
    >
      <div className="mx-auto z-20 flex gap-2 p-2 items-center">
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={handleZoomIn}
          title="Zoom In"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={handleZoomOut}
          title="Zoom Out"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={handleReset}
          title="Reset View"
        >
          <RotateCw className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={toggleFullscreen}
          title="Toggle Fullscreen"
        >
          <Maximize className="h-4 w-4" />
        </Button>
        <div>{Math.round(scale * 100)}%</div>
      </div>

      <div
        className="w-full h-full flex items-center justify-center overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          cursor: isDragging ? "grabbing" : scale > 1 ? "grab" : "default",
        }}
      >
        <div
          className="relative"
          style={{
            transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
            transformOrigin: "center",
            transition: "transform 100ms",
          }}
        >
          <img
            ref={imageRef}
            src={signedUrl}
            alt={alt || "Document preview"}
            className="max-h-full max-w-full object-contain"
            draggable="false"
            onLoad={(e) => {
              setCanvasHeight?.(e.currentTarget.clientHeight);
              setCanvasWidth?.(e.currentTarget.clientWidth);
            }}
          />
          {boundingBox &&
            (() => {
              const rect = convertBoundingBoxToRect(boundingBox);
              return (
                <div
                  className="absolute pointer-events-none border-1 border-blue-500 border-dashed bg-blue-500/10 z-10"
                  style={{
                    top: rect.y,
                    left: rect.x,
                    width: rect.width,
                    height: rect.height,
                  }}
                />
              );
            })()}
        </div>
      </div>
    </div>
  );
};

export const ExtractionFileViewer: React.FC<FileViewerProps> = ({
  signedUrl,
  fileType,
  fileName,
  initialPage,
  boundingBox,
  elementId,
  pageWidth,
  pageHeight,
  defaultSearchValue,
  documentExtraction,
  allowLinking,
  onLinkingComplete,
}) => {
  // Track the current page internally
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Track rendered canvas size reported by the underlying viewer
  const [canvasWidth, setCanvasWidth] = useState<number>(0);
  const [canvasHeight, setCanvasHeight] = useState<number>(0);

  const scaledBoundingBox: BoundingBox | undefined = useMemo(() => {
    if (!boundingBox) return undefined;
    const scaleX = canvasWidth / (pageWidth ?? 1);
    const scaleY = canvasHeight / (pageHeight ?? 1);

    return [
      [boundingBox[0][0] * scaleX, boundingBox[0][1] * scaleY],
      [boundingBox[1][0] * scaleX, boundingBox[1][1] * scaleY],
      [boundingBox[2][0] * scaleX, boundingBox[2][1] * scaleY],
      [boundingBox[3][0] * scaleX, boundingBox[3][1] * scaleY],
    ];
  }, [boundingBox, pageWidth, pageHeight, canvasWidth, canvasHeight]);

  // Update internal state when initialPage changes
  useEffect(() => {
    if (initialPage !== undefined && initialPage !== currentPage) {
      setCurrentPage(initialPage);
    }
  }, [currentPage, initialPage]);

  const renderViewer = () => {
    switch (fileType?.toLowerCase()) {
      case "pdf":
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            }
          >
            <ExtractionPdfViewer
              key={`${signedUrl}-${currentPage}`}
              signedUrl={signedUrl}
              initialPage={currentPage}
              boundingBox={
                scaledBoundingBox
                  ? convertBoundingBoxToRect(scaledBoundingBox)
                  : undefined
              }
              setCanvasHeight={(height) => {
                setCanvasHeight(height);
              }}
              setCanvasWidth={(width) => {
                setCanvasWidth(width);
              }}
              documentExtraction={documentExtraction}
              allowLinking={allowLinking}
              onLinkingComplete={onLinkingComplete}
            />
          </Suspense>
        );
      case "csv":
        return (
          <CSVViewer
            elementId={elementId}
            url={signedUrl}
            defaultSearchValue={elementId ? undefined : defaultSearchValue}
          />
        );
      // .xlsm (macro-enabled) shares the .xlsx OOXML format; ExcelViewer renders
      // it identically. Macros are not executed in a viewer.
      case "xlsm":
      case "xlsx":
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            }
          >
            <ExcelViewer
              fileName={fileName}
              url={signedUrl}
              elementId={elementId}
            />
          </Suspense>
        );
      case "jpeg":
      case "png":
      case "webp":
        return (
          <ImageViewer
            signedUrl={signedUrl}
            boundingBox={scaledBoundingBox}
            alt={fileName}
            setCanvasHeight={setCanvasHeight}
            setCanvasWidth={setCanvasWidth}
          />
        );
      default:
        return (
          <Alert variant="destructive">
            <AlertTitle>Unsupported File Type</AlertTitle>
            <AlertDescription>
              The file type {fileType} is not supported for viewing.
            </AlertDescription>
          </Alert>
        );
    }
  };

  return renderViewer();
};

export default ExtractionFileViewer;
